// App.js - Main application component with improved book content handling
import React, { useState, useEffect } from 'react';
import './App.css';
import AgentSelection from './components/AgentSelection';
import ChatInterface from './components/ChatInterface';
import { bookContentData } from './data/bookContent';

const App = () => {
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState('');

  const handleAgentSelect = (agent) => {
    setSelectedAgent(agent);
    
    // Use bookId instead of bookTitle for more reliable lookup
    const bookId = agent.bookId;
    const bookInfo = bookContentData[bookId];
    
    if (!bookInfo) {
      console.error(`Book not found for ID: ${bookId}`);
      // Fallback to using the title as before
      const bookInfoByTitle = bookContentData[agent.bookTitle];
      if (!bookInfoByTitle) {
        console.error(`Book also not found by title: ${agent.bookTitle}`);
      }
    }
    
    // Get book content for the selected agent (using ID with title as fallback)
    const bookData = bookInfo || bookContentData[agent.bookTitle] || {
      summary: "Information about this book is currently being updated.",
      keyPoints: ["AI innovation", "Digital transformation", "Customer experience"]
    };
    
    // Create a comprehensive representation of the book content
    let bookPrompt = `
Summary:
${bookData.summary}

Key Points:
`;

    // Process key points with better handling of complex objects
    bookData.keyPoints.forEach((point, index) => {
      if (typeof point === 'string') {
        bookPrompt += `${index + 1}. ${point}\n`;
      } else if (typeof point === 'object') {
        // Handle table of contents if present
        if (point.tableOfContents) {
          bookPrompt += `${index + 1}. Table of Contents:\n${point.tableOfContents.map(item => `   - ${item}`).join('\n')}\n`;
        }
        
        // Include full introduction if present
        if (point.introduction) {
          bookPrompt += `\nIntroduction:\n${point.introduction}\n`;
        }
        
        // Include full executive summary if present
        if (point.executiveSummary) {
          bookPrompt += `\nExecutive Summary:\n${point.executiveSummary}\n`;
        }
      }
    });
    
    // Create system prompt with complete book info
    const newSystemPrompt = `You are ${agent.name}, an AI assistant specialized in the book "${agent.bookTitle}". ${agent.description}
    
The following is comprehensive information about the book content that you should use to inform your responses:

${bookPrompt}

When answering, include relevant information from the book where possible. Format your responses using Markdown for better readability. For code examples, use proper code blocks with language specification.`;
    
    setSystemPrompt(newSystemPrompt);
    
    // Add a welcome message
    setMessages([
      {
        role: 'assistant',
        content: `Hello! I'm ${agent.name}, your expert on "${agent.bookTitle}". I'm ready to discuss key topics from the book and answer your questions. How can I help you today?`
      }
    ]);
  };

  const handleSendMessage = async (message) => {
    if (!message.trim()) return;

    // Add user message to chat
    const updatedMessages = [
      ...messages,
      { role: 'user', content: message }
    ];
    setMessages(updatedMessages);
    setIsLoading(true);

    try {
      // Prepare the messages for Claude API
      const apiMessages = updatedMessages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      // Create request payload - note the system prompt is added only once
      const requestPayload = {
        model: 'claude-3-5-haiku-20241022',
        messages: apiMessages,
        system: systemPrompt, // Use the pre-set system prompt
        max_tokens: 4000
      };

      // Log a more useful debug message
      console.log("Sending to API with system prompt of length:", systemPrompt.length);
      console.log("First 200 chars:", systemPrompt.substring(0, 200) + "...");
      console.log("Last 200 chars:", "..." + systemPrompt.substring(systemPrompt.length - 200));

      // Call the Claude API (via your secure backend proxy)
      const response = await fetch('https://9x2f36f5la.execute-api.eu-central-2.amazonaws.com/prod/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestPayload),
      });
      
      const data = await response.json();
      
      // Handle different response formats
      let assistantContent = '';
      let citations = [];
      
      if (data) {
        // Direct Claude API response format
        if (data.content && Array.isArray(data.content) && data.content.length > 0) {
          assistantContent = data.content[0].text;
          citations = data.citations || [];
        } 
        // Lambda might wrap the Claude response
        else if (data.body) {
          try {
            // Try to parse the body if it's a string
            const bodyData = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
            
            if (bodyData.content && Array.isArray(bodyData.content) && bodyData.content.length > 0) {
              assistantContent = bodyData.content[0].text;
              citations = bodyData.citations || [];
            }
          } catch (e) {
            assistantContent = 'Error parsing the response from Claude.';
          }
        }
        // If we still don't have content but have error message
        else if (data.error) {
          assistantContent = `Error from API: ${data.error}`;
        } else {
          assistantContent = 'Received a response but couldn\'t extract the message content.';
        }
      } else {
        assistantContent = 'No response data received from the API.';
      }

      // Add Claude's response to the chat
      setMessages([
        ...updatedMessages,
        { 
          role: 'assistant', 
          content: assistantContent,
          citations: citations
        }
      ]);
    } catch (error) {
      setMessages([
        ...updatedMessages,
        { role: 'assistant', content: `Sorry, I encountered an error: ${error.message}` }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="app">
      {selectedAgent ? (
        <ChatInterface 
          agent={selectedAgent}
          messages={messages}
          onSendMessage={handleSendMessage}
          isLoading={isLoading}
          onBack={() => setSelectedAgent(null)}
        />
      ) : (
        <AgentSelection onSelectAgent={handleAgentSelect} />
      )}
    </div>
  );
};

export default App;